import { ActionList } from '../../../../common/models/action-list.model';
import {
  TableColumns,
  TableFilterTab,
} from '../../../../../ui-components/lib/table/table.model';
import { DelegationStatus } from './delegation.model';
import { FilterOption } from '../../my-inbox/contributed-tasks/contributed-tasks.imports';

export const DELEGATION_TAB_KEYS = {
  DELEGATED_BY_ME: 'delegated_by_me',
  DELEGATED_TO_ME: 'delegated_to_me',
} as const;

export const DELEGATION_TAB_FILTER_OPTIONS: TableFilterTab[] = [
  {
    label: 'delegation_tab_filter_option_delegated_by_me',
    key: DELEGATION_TAB_KEYS.DELEGATED_BY_ME,
    identifier: DELEGATION_TAB_KEYS.DELEGATED_BY_ME,
    iconName: 'circle-arrow-up',
    active: true,
  },
  {
    label: 'delegation_tab_filter_option_delegated_to_me',
    key: DELEGATION_TAB_KEYS.DELEGATED_TO_ME,
    identifier: DELEGATION_TAB_KEYS.DELEGATED_TO_ME,
    iconName: 'circle-arrow-down',
    active: false,
  },
];

export const DELEGATION_PERSON_COLUMN_HEADER: Record<
  (typeof DELEGATION_TAB_KEYS)[keyof typeof DELEGATION_TAB_KEYS],
  string
> = {
  [DELEGATION_TAB_KEYS.DELEGATED_BY_ME]: 'delegation_tab_column_delegatee',
  [DELEGATION_TAB_KEYS.DELEGATED_TO_ME]: 'delegation_tab_column_delegated_by',
};

export const DELEGATION_PERSON_COLUMN_SORT_KEY: Record<
  (typeof DELEGATION_TAB_KEYS)[keyof typeof DELEGATION_TAB_KEYS],
  string
> = {
  [DELEGATION_TAB_KEYS.DELEGATED_BY_ME]: 'delegate][type',
  [DELEGATION_TAB_KEYS.DELEGATED_TO_ME]: 'delegator][type',
};

export const DELEGATION_COLUMN_IDENTIFIERS = {
  NUMBER: 'number',
  ID: 'id',
  DELEGATE: 'delegate',
  PERIOD: 'period',
  TASKS: 'tasks',
  STATUS: 'status',
  ACTION: 'action',
} as const;

export const COLUMNS: TableColumns[] = [
  {
    header: '',
    identifier: DELEGATION_COLUMN_IDENTIFIERS.NUMBER,
  },
  {
    header: 'delegation_tab_column_id',
    identifier: DELEGATION_COLUMN_IDENTIFIERS.ID,
    showViewIcon: true,
    isSortable: true,
    sortKey: 'id][type',
  },
  {
    header: 'delegation_tab_column_delegatee',
    identifier: DELEGATION_COLUMN_IDENTIFIERS.DELEGATE,
    isSortable: true,
    sortKey: 'delegate][type',
  },
  {
    header: 'delegation_tab_column_period',
    identifier: DELEGATION_COLUMN_IDENTIFIERS.PERIOD,
    isSortable: true,
    sortKey: 'started_at][type',
  },
  {
    header: 'delegation_tab_column_tasks',
    identifier: DELEGATION_COLUMN_IDENTIFIERS.TASKS,
    isSortable: true,
    sortKey: 'tasks][type',
  },
  {
    header: 'delegation_tab_column_status',
    identifier: DELEGATION_COLUMN_IDENTIFIERS.STATUS,
    isSortable: true,
    sortKey: 'state][type',
  },
  {
    header: '',
    identifier: DELEGATION_COLUMN_IDENTIFIERS.ACTION,
  },
];

export const DELEGATION_STATUS_BADGE_CLASS: Record<DelegationStatus, string> = {
  [DelegationStatus.UPCOMING]: 'bg-primary-500',
  [DelegationStatus.ACTIVE]: 'bg-warning-500',
  [DelegationStatus.CANCELLED]: 'bg-neutral-900',
  [DelegationStatus.COMPLETED]: 'bg-success-500',
  [DelegationStatus.REVOKED]: 'bg-destructive-500',
};

export const DELEGATION_VIEW_DETAILS_ACTION: ActionList = {
  key: 'view_details',
  hasIcon: true,
  label: 'view_details_action_item',
  hasDivider: false,
  labelCustomStyle: '',
  hasToggle: false,
  toggleValue: false,
  isLink: false,
};

export const DELEGATION_EDIT_ACTION: ActionList = {
  key: 'edit',
  hasIcon: true,
  label: 'edit_action_item',
  hasDivider: false,
  labelCustomStyle: '',
  hasToggle: false,
  toggleValue: false,
  isLink: false,
};

export const DELEGATION_CANCEL_ACTION: ActionList = {
  key: 'cancel',
  hasIcon: true,
  label: 'cancel_action_item',
  hasDivider: true,
  labelCustomStyle: 'text-destructive-500',
  hasToggle: false,
  toggleValue: false,
  isLink: false,
};

export const DELEGATION_REVOKE_ACTION: ActionList = {
  key: 'revoke',
  hasIcon: true,
  label: 'revoke_action_item',
  hasDivider: true,
  labelCustomStyle: 'text-destructive-500',
  hasToggle: false,
  toggleValue: false,
  isLink: false,
};

export const DELEGATION_FILTER_OPTIONS: FilterOption[] = [
  {
    key: 'state',
    label: 'delegation_tab_filter_option_status',
    type: 'single',
    placeholder: 'delegation_tab_filter_option_status_placeholder',
    options: [
      {
        value: DelegationStatus.UPCOMING,
        label: 'delegation_tab_filter_option_status_upcoming',
      },
      {
        value: DelegationStatus.ACTIVE,
        label: 'delegation_tab_filter_option_status_active',
      },
      {
        value: DelegationStatus.CANCELLED,
        label: 'delegation_tab_filter_option_status_cancelled',
      },
      {
        value: DelegationStatus.COMPLETED,
        label: 'delegation_tab_filter_option_status_completed',
      },
      {
        value: DelegationStatus.REVOKED,
        label: 'delegation_tab_filter_option_status_revoked',
      },
    ],
  },
  {
    key: 'date',
    label: 'delegation_tab_filter_option_period',
    type: 'dateRange',
  }
];

export const DELEGATION_EDITABLE_STATUSES: DelegationStatus[] = [
  DelegationStatus.UPCOMING,
  DelegationStatus.ACTIVE,
];

export function getDelegationRowActions(
  status: DelegationStatus,
  isDelegatedToMe = false
): ActionList[] {
  const actions: ActionList[] = [{ ...DELEGATION_VIEW_DETAILS_ACTION }];

  if (isDelegatedToMe) {
    return actions;
  }

  if (DELEGATION_EDITABLE_STATUSES.includes(status)) {
    actions.push({ ...DELEGATION_EDIT_ACTION });
  }

  if (status === DelegationStatus.UPCOMING) {
    actions.push({ ...DELEGATION_CANCEL_ACTION });
  }

  if (status === DelegationStatus.ACTIVE) {
    actions.push({ ...DELEGATION_REVOKE_ACTION });
  }

  return actions;
}
